import React, { useContext } from 'react';

import { BorderBox, ButtonPrimary, Box, Button, ButtonGroup, Text, Heading, Dropdown, ProgressBar, Details } from '@primer/components'

import { normalizeDiff } from '../../components/Advice'

import { CorpusContext } from '../../hooks/useCorpus'
import { useStore, getIsAuthenticated } from '../../Store'

import GithubPermissionDenied from '../../components/GithubPermissionDenied'
import Card from '../../components/BeforeAfter/Card'
import GrantPublicAccessButton from '../../components/GrantPublicAccessButton'

export const groupMessages = function(messages) {
  let group = new Map()
  let path;
  for (let message of messages) {
    if (message.message_type === 'line_centric') {
        path = message.path
        if (!group.has(path)) {
          group.set(path, [])
        }
        group.get(path).push(message)
    }
  }
  return Array.prototype.concat.apply([], Array.from(group.values()));
}


export default function FixItForMe({
  setState,
  messages,
  activeMessageIndex,
  acceptedChanges,
  skippedChanges,
  isCheckInProgress,
  isCreatePullRequestPermissionDenied,
  handleSave,
  style,
  isFinshDisabled,
}) {
    const groupedMessages = groupMessages(messages)

    if (groupedMessages.length === 0) {
      if (isCheckInProgress) {
        return <p className="container-lg lead">Auditing codebase. Please wait.</p>
      } else {
        return <p className="container-lg lead">No issue found that can be auto fixed.</p>
      }
    }
    function findNextMessageIndex(nextAcceptedChanges, nextSkippedChanges) {
      for (let [i, item] of groupedMessages.entries()) {
        if (nextAcceptedChanges.indexOf(item) === -1 && nextSkippedChanges.indexOf(item) === -1) {
          return i
        }
      }
    }

    function handleAcceptOne(item) {
      const nextAcceptedChanges = acceptedChanges.concat([item])
      setState({
        acceptedChanges: nextAcceptedChanges,
        activeMessageIndex: findNextMessageIndex(nextAcceptedChanges, skippedChanges),
        skippedChanges,
      })
    }

    function handleSkipOne(item) {
      const nextSkippedChanges = skippedChanges.concat([item])
      setState({
        acceptedChanges,
        activeMessageIndex: findNextMessageIndex(acceptedChanges, nextSkippedChanges),
        skippedChanges: nextSkippedChanges,
      })
    }

    function handleAcceptAllClick(item) {
      const nextAcceptedChanges = acceptedChanges.concat([])
      const similarMessages = getSimilarMessage(item.message_ids[0])
      for (let item of similarMessages) { 
        if (!nextAcceptedChanges.includes(item)) {
          nextAcceptedChanges.push(item)
        }
      }
      setState({
        acceptedChanges: nextAcceptedChanges,
        activeMessageIndex: findNextMessageIndex(nextAcceptedChanges, skippedChanges),
        skippedChanges,
      })
    }

    function handleSkipAllClick(item) {
      const nextSkippedChanges = skippedChanges.concat([])
      const similarMessages = getSimilarMessage(item.message_ids[0])
      for (let item of similarMessages) { 
        if (!nextSkippedChanges.includes(item)) {
          nextSkippedChanges.push(item)
        }
      }
      setState({
        acceptedChanges,
        activeMessageIndex: findNextMessageIndex(acceptedChanges, nextSkippedChanges),
        skippedChanges: nextSkippedChanges,
      })
    }

    function handleClickSave() {
      const whitelist = {}
      for (let message of acceptedChanges) {
        whitelist[message.path] = whitelist[message.path] || {}
        whitelist[message.path][message.line] = message.message_ids
      }
      // the whitelist contains 0000 then that line will be marked as skipping those message ids
      for (let message of skippedChanges) {
        whitelist[message.path] = whitelist[message.path] || {}
        whitelist[message.path][message.line] = ['0000'].concat(message.message_ids)
      }
      handleSave(whitelist)
    }

    function getSimilarMessage(messageId) {
      return groupedMessages.filter(item => 
        item.message_ids.length === 1 &&
        item.message_ids[0] === messageId &&
        acceptedChanges.indexOf(item) === -1 &&
        skippedChanges.indexOf(item) === -1
      )
    }

    const message = groupedMessages[activeMessageIndex]
    const similarMessages = (message && message.message_ids.length === 1) ? getSimilarMessage(message.message_ids[0]) : []
    const processedChanges = acceptedChanges.length + skippedChanges.length
    const boxStyle = {
      minHeight: '500px',
      background: 'rgba(0, 0, 0, 0) linear-gradient(to right, rgb(54, 55, 149), rgb(0, 92, 151)) repeat scroll 0% 0%',
      alignItems: 'center',
      justifyContent: 'center',
      ...style,
    }
    return (
      <Box className="pb-6 pt-md-6 d-md-flex" style={boxStyle}>
        { isCreatePullRequestPermissionDenied && <GithubPermissionDenied /> }
        {
          message
            ? (
              <Cards
                message={message}
                handleAcceptOne={handleAcceptOne}
                handleSkipOne={handleSkipOne}
                handleAcceptAllClick={handleAcceptAllClick}
                handleSkipAllClick={handleSkipAllClick}
                similarCount={similarMessages.length}
                messageCount={groupedMessages.length}
                handleClickSave={handleClickSave}
                processedChanges={processedChanges}
              />
            )
            : (
                <EndCard
                  acceptedCount={acceptedChanges.length}
                  skippedCount={skippedChanges.length}
                  handleClickSave={handleClickSave}
                  isFinshDisabled={isFinshDisabled}
                />
            )
        }
      </Box>
    )
}

function Cards(props) {
  const { corpus } = useContext(CorpusContext)
  const progress = (100 / props.messageCount) * props.processedChanges
  return (
    <BorderBox className="bg-white col-md-6">
      <Box className="bg-gray p-2 border-bottom border-gray-dark d-inline-flex width-full" style={{alignItems: "center", gap: "12px"}}>
        <Text><Text className="hide-sm">Issue</Text> <b>{ props.processedChanges + 1 }</b> of <b>{ props.messageCount }</b></Text>
        <ProgressBar style={{flex: 1}} progress={progress}/>
      </Box>
      <Box className="p-md-4 pt-4">
        <Box>
          <Card
            exampleTitle={props.message.path}
            exampleBefore={normalizeDiff(props.message.diff.before)}
            exampleAfter={normalizeDiff(props.message.diff.after)}
            code={props.message.message_ids[0]}
            className="text-left"
          />
        </Box>
        <Box className="mt-4">
          {
            props.message.message_ids.map((messageId, i) => {
              const { explanation, title, Summary} = corpus[messageId]
              return (     
                <Box key={i} className="mb-4">
                  <Heading fontSize={3} className="mb-2">{title}</Heading>
                  <Box className={'f4 d-inline'}>
                    <Summary />
                  </Box>
                  {
                    explanation && (
                      <Details>
                        <Text as="summary"><a href="#">Read more</a></Text>
                        {explanation}
                      </Details>
                    )
                  }
                </Box>
              )
            })
          }
        </Box>
      </Box>
      <Box className="bg-gray text-right p-2 border-top border-gray-dark d-flex" justifyContent="flex-end">
        <ButtonGroup className="text-left">
          {
            props.similarCount > 1 && (
              <Dropdown width="42px">
                <Dropdown.Button style={{paddingLeft: 0, borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 0}}>&nbsp;</Dropdown.Button>
                <Dropdown.Menu direction='sw'>
                  <Dropdown.Item style={{ cursor: 'pointer' }} onClick={() => props.handleAcceptAllClick(props.message)}>Accept all {props.similarCount} like this</Dropdown.Item>
                  <Dropdown.Item style={{ cursor: 'pointer' }} onClick={() => props.handleSkipAllClick(props.message)}>Skip all {props.similarCount} like this</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )
          }
          <Button style={{borderRight: 0, borderLeftWidth: "1px"}} onClick={() => props.handleSkipOne(props.message)}>Skip</Button>
          <ButtonPrimary style={{borderLeft: 0}} onClick={() => props.handleAcceptOne(props.message)}>Accept change</ButtonPrimary>
        </ButtonGroup>
      </Box>
    </BorderBox>
  )
}

function EndCard({acceptedCount, skippedCount, handleClickSave, isFinshDisabled}) {
  const { state } = useStore()
  const isAuthenticated = getIsAuthenticated(state)

  return (
    <BorderBox className="bg-white d-inline-block">
      <Box className="bg-gray p-2 border-bottom border-gray-dark d-inline-flex width-full" style={{alignItems: "center", gap: "12px"}}>
        <b>Change summary</b>
      </Box>
      <Box className="d-inline-block p-4 f3">
        <Text>You have chosen to fix {acceptedCount} {acceptedCount > 1 ? 'issues' : 'issue'}</Text>
        {skippedCount > 0 && <Text> and skip {skippedCount} {skippedCount > 1 ? 'issues' : 'issue'}</Text>}
        <Text>.</Text>
      </Box>
      <Box className="bg-gray p-2 border-top border-gray-dark text-center">
        {
          isAuthenticated
            ? <ButtonPrimary onClick={handleClickSave} disabled={isFinshDisabled}>Finish and save</ButtonPrimary>
            : <GrantPublicAccessButton label="Login to GitHub to save" onLogin={handleClickSave} />
        }
      </Box>
    </BorderBox>
  )
}
